import { v4 as uuidv4 } from 'uuid';
import {
    MED_MANAGEMENT_FLOW,
    MESSAGE_ASSIST_IFRAME_ID,
    MESSAGE_ASSIST_MESSAGE_EVENT_TYPES,
    MESSAGE_ASSIST_SCHEMA,
    TENANTS,
} from './constants';
import general from './resources/general.json';
import {
    getUrlParam,
    getWindowObject,
    updateFaqSurveyOptionEnabled,
} from './helpers';
import { ButtonData } from './jsxElements';
import { logException, logDebugInsights, flushInsightsAsync } from './logging';
import { craftCustomActivityMessage } from './messageUtils';
import { Activity, Attachment, TenantName } from './types';
import { sendExternalLinkSelected } from './analytics';
import { AmpFlagSet, amplitudeClients } from './amplitude';
import { FaqData, getFaqDataForIntent } from './attachments/MessageAssist';
import getTypeSafeErrorMessage from './utils/getTypeSafeErrorMessage';
import { ProvidenceButtonListButton } from './attachments';
import { GraceFeatureFlags } from './featureFlags';

type MessageAssistResponse = {
    accessToken?: string;
    [key: string]: unknown;
};

type PromiseResolveReject = {
    resolve: (data: MessageAssistResponse) => void;
    reject: (err: string) => void;
};

const messageassistRequests: Map<string, PromiseResolveReject> = new Map();
const inIframe = () => window.self !== window.top;

export const createMADeepLinkButton = (
    title: string,
    link: string,
    intent: string
): ButtonData => {
    return {
        action: `${MESSAGE_ASSIST_SCHEMA}${MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.DEEP_LINK}`,
        title,
        data: JSON.stringify({ link: link }),
        showPunchOutIcon: true,
        intent,
        showSurvey: true,
    };
};

const createMACancelMessageButton = (ctaType?: string): ButtonData => {
    const title = general.providence.cancelMessage;
    const data = '{}';

    return {
        action: `${MESSAGE_ASSIST_SCHEMA}${MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.CANCEL_MESSAGE}`,
        title: title,
        data: data,
        showPunchOutIcon: false,
        returnToMenu: true,
        ctaType: ctaType,
    };
};

const createMAChatWithAIButton = (): ButtonData => {
    const title = general.providence.inputMenuPlaceholder;

    return {
        title: title,
        data: title,
        showPunchOutIcon: false,
        returnToMenu: true,
    };
};

// Wrapper function to create a button with "Help me with my request" title by default
// Click on the button will proceeed with the flow identified for the given utterance.
// For example: if the utterance is "Dr Smith, I need a referral", it will trigger
// the referral flow
const createRequestHelpMessageButton = (
    utterance: string,
    title: string = general.providence.helpMeWithMyRequest,
    ctaType?: string
): ButtonData => {
    return {
        action: undefined,
        title: title,
        data: utterance,
        showPunchOutIcon: false,
        returnToMenu: false,
        ctaType: ctaType,
    };
};

const createMACallToActionButton = (
    answer: string,
    intent: string
): ButtonData => {
    if (!answer) {
        return createMACancelMessageButton();
    }

    // Look for markdown hyperlinks within the response text
    // Example: [Appointments](https://mychartor.providence.org/mychart/Visits)
    const match = answer.match(new RegExp('\\[([^\\]]+)\\]\\(([^\\)]+)\\)'));

    // If a markdown hyperlink is found within the response text, create a button
    // from that hyperlink. Otherwise, create a button to cancel the MA flow.
    if (match) {
        if (match.length < 3) {
            return createMACancelMessageButton();
        } else {
            const title = match[1];
            const link = match[2];
            return createMADeepLinkButton(title, link, intent);
        }
    }
    return createMACancelMessageButton();
};

export const createMASendMessageButton = (
    body: string,
    intent: string,
    faqData?: FaqData,
    sendText?: string,
    ctaType?: string,
    showCategories?: boolean
): ButtonData => {
    if (!intent) {
        // TODO: Create an app insights log for this
    }

    const buttonData = {
        action: `${MESSAGE_ASSIST_SCHEMA}${MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.SEND_MESSAGE}`,
        title: sendText ? sendText : general.providence.messageProvider,
        data: JSON.stringify({
            category: {
                categoryId: '11',
                categoryLabel: faqData?.title,
                messageType: '11',
            },
            subject: faqData?.subject || faqData?.title,
            body: body || '',
            intent: intent || '',
            showCategories: showCategories || false,
        }),
        showPunchOutIcon: false,
    };

    if (ctaType) {
        // TODO: Re-think the way we pass data through
        // ButtonData/ButtonInfo/ProvidenceButtonListButton.
        // The current design relies on distinct
        // models to be compatibles and cast into one or another type as we need.
        const providenceButtonListButton =
            buttonData as ProvidenceButtonListButton;
        providenceButtonListButton.ctaType = ctaType;
    }

    return buttonData;
};

export const craftMAFaqMessage = (
    answer: string,
    intent: string,
    source: string,
    utterance: string,
    faqData?: FaqData
): Activity => {
    return craftCustomActivityMessage(
        undefined,
        undefined,
        [
            {
                contentType: 'application/providence.messageAssistQnaResponse',
                content: {
                    qnaAnswer: answer,
                    qnaIntent: intent,
                    qnaSource: source,
                    actions: [
                        createMACallToActionButton(answer, intent),
                        createMASendMessageButton(utterance, intent, faqData),
                    ],
                    removeBubble: true,
                },
            },
        ],
        intent,
        source,
        true
    );
};

export const createDefaultMAButtons = (
    body: string,
    sendText?: string,
    faqData?: FaqData,
    intent = ''
): ButtonData[] => {
    return [
        createMASendMessageButton(body, intent, faqData, sendText, 'primary'),
        createMACancelMessageButton('secondary'),
    ];
};

export const craftMAFallbackErrorMessage = (utterance: string): Activity => {
    return craftMAFallbackMessage(
        utterance,
        '',
        '',
        undefined,
        general.providence.messageAssistFallbackError
    );
};

export const craftMAFallbackMessage = (
    utterance: string,
    intent: string,
    source: string,
    faqData?: FaqData,
    text: string = general.providence.messageAssistFallback
): Activity => {
    const sendText = general.providence.continueWithMessage;

    return craftCustomActivityMessage(
        text,
        'ma_fallback',
        [
            {
                contentType: 'application/providence.buttonlist',
                content: {
                    buttons: createDefaultMAButtons(
                        utterance,
                        sendText,
                        faqData,
                        intent
                    ),
                },
            },
        ],
        intent,
        source,
        true
    );
};

export const craftMARedirectFollowupMessage = (
    utterance: string,
    intent: string,
    source: string,
    faqData?: FaqData,
    showCategories?: boolean
): Activity => {
    const continueWithAIButton = createMAChatWithAIButton();
    const sendButton = createMASendMessageButton(
        utterance,
        intent,
        faqData,
        general.providence.continueWithMessage,
        undefined,
        showCategories
    );

    const providenceButtonSendButton = sendButton as ProvidenceButtonListButton;
    providenceButtonSendButton.ctaType = 'secondary';

    return craftCustomActivityMessage(
        general.providence.sendMessageForRedirect,
        'ma_fallback',
        [
            {
                contentType: 'application/providence.buttonlist',
                content: {
                    buttons: [continueWithAIButton, sendButton],
                },
            },
        ],
        intent,
        source,
        true
    );
};

export const craftMANotProviderMessage = (
    utterance: string,
    intent: string,
    source: string
): Activity => {
    const text = general.providence.messageAssistNotProvider;
    const sendText = general.providence.continueWithMessage;
    return craftCustomActivityMessage(
        text,
        undefined,
        [
            {
                contentType: 'application/providence.buttonlist',
                content: {
                    buttons: createDefaultMAButtons(
                        utterance,
                        sendText,
                        undefined,
                        ''
                    ),
                },
            },
        ],
        intent,
        source,
        true
    );
};

export const craftMANotProviderMessageWithFulfillment = (
    utterance: string,
    intent: string,
    source: string,
    featureFlags: AmpFlagSet
): Activity => {
    const text = general.providence.messageAssistNotProviderWithFulfillment;

    if (featureFlags[GraceFeatureFlags.EnableFulfillmentOptionWithDearDoctor]) {
        const intentFaqData = getFaqDataForIntent(intent, featureFlags);

        return craftCustomActivityMessage(
            text,
            undefined,
            [
                {
                    contentType: 'application/providence.buttonlist',
                    content: {
                        buttons: [
                            createRequestHelpMessageButton(
                                utterance,
                                intentFaqData?.helpMeRequest ||
                                    general.providence.helpMeWithMyRequest,
                                'primary'
                            ),
                            createMASendMessageButton(
                                utterance,
                                '',
                                undefined,
                                general.providence.skipAndSendMessage,
                                'secondary'
                            ),
                        ],
                    },
                },
            ],
            intent,
            source,
            true
        );
    }

    const sendText = general.providence.skipAndWriteAMessage;

    return craftCustomActivityMessage(
        text,
        undefined,
        [
            {
                contentType: 'application/providence.buttonlist',
                content: {
                    buttons: [
                        createRequestHelpMessageButton(utterance),
                        createMASendMessageButton(
                            utterance,
                            '',
                            undefined,
                            sendText,
                            'secondary'
                        ),
                    ],
                },
            },
        ],
        intent,
        source,
        true
    );
};

export const postAnchorClickEventToParentApp = async (
    e: React.MouseEvent<HTMLElement>,
    href: string,
    text: string
) => {
    // basic validation of href before doing anything
    if (!href || !(href.startsWith('https://') || href.startsWith('tel:'))) {
        return;
    }

    try {
        sendExternalLinkSelected(text, href);
        await postMessageAssistMessage(
            MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.DEEP_LINK,
            text,
            JSON.stringify({ link: href })
        );

        // Only stop event propagation if message posting is successful
        e.preventDefault();
        e.stopPropagation();
    } catch (error: unknown) {
        logException('Message Assist - Anchor handle error', {
            exception: new Error(getTypeSafeErrorMessage(error)),
        });
    }
};

// in the case of an outage, message assist should just skip the chatbot flow completely (go directly to send message).
export const handleMAChatbotOutage = (
    tenant: TenantName,
    outageMessage = 'Chatbot Outage'
) => {
    if (tenant === TENANTS.MESSAGE_ASSIST) {
        postMessageAssistMessage(
            `${MESSAGE_ASSIST_SCHEMA}${MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.ON_GRACE_LOAD_FAIL}`,
            undefined,
            JSON.stringify({ description: outageMessage })
        ).catch((error: unknown) => {
            logException('Message Assist - Outage handling error', {
                exception: new Error(getTypeSafeErrorMessage(error)),
            });
        });
    }
};

export class MessageAssistActionType {
    readonly type: string;
    readonly triggersUnload: boolean;
    readonly updatedData: string;

    constructor(type: string, triggersUnload = false, updatedData = '') {
        this.type = type;
        this.triggersUnload = triggersUnload;
        this.updatedData = updatedData;
    }

    static fromString(
        messageType: string,
        data: string
    ): MessageAssistActionType {
        const index = messageType.indexOf(MESSAGE_ASSIST_SCHEMA);
        let type: string;
        const initialData: string = data;

        if (index < 0) {
            type = messageType;
        } else {
            type = messageType.substring(index + MESSAGE_ASSIST_SCHEMA.length);
        }
        if (type === '') {
            throw Error('Undefined message type to post');
        }
        const { CANCEL_MESSAGE, SEND_MESSAGE } =
            MESSAGE_ASSIST_MESSAGE_EVENT_TYPES;

        // Default to false for types (e.g. DEEP_LINK, REQUEST_ACCESS_TOKEN, ERROR) that don't trigger MA unload
        let triggersUnload = false;

        switch (type) {
            case CANCEL_MESSAGE:
            case SEND_MESSAGE:
                triggersUnload = true;
                data = createSurveyableUtterranceEventData(initialData);
                break;
        }

        return new MessageAssistActionType(type, triggersUnload, data);
    }
}

export const createSurveyableUtterranceEventData = (data: string): string => {
    if (!data) {
        return data;
    }
    // Setting the default to false for showSurveyForCategory
    let showSurveyForCategory = false;
    try {
        const dataObj = JSON.parse(data);
        if (dataObj) {
            if (dataObj.intent) {
                showSurveyForCategory = isIntentSurveyable(dataObj.intent);
            } else {
                showSurveyForCategory = isFlowSurveyable(dataObj.flow);
            }
            dataObj.showSurveyForCategoryBasedOnUtterance =
                showSurveyForCategory;
        }

        data = JSON.stringify(dataObj);
    } catch (error: unknown) {
        logException('Message Assist - Error parsing utterance data', {
            exception: new Error(getTypeSafeErrorMessage(error)),
        });
    }

    return data;
};

export const isIntentSurveyable = (intent: string): boolean => {
    const faqData = getFaqDataForIntent(intent);
    return faqData?.showSurveyForCategory ?? false;
};

export const isFlowSurveyable = (flow: string): boolean => {
    if (!flow) {
        return false;
    }
    const flowNameWithoutSubflow = flow.split('-')[0].trim();
    return [MED_MANAGEMENT_FLOW].includes(
        flowNameWithoutSubflow?.toLowerCase()
    );
};

export const postMessageAssistMessage = async (
    messageType: string,
    title?: string,
    data?: string
) => {
    const { type, triggersUnload, updatedData } =
        MessageAssistActionType.fromString(messageType, data ?? '');

    data = updatedData;
    if (triggersUnload) {
        // flush amplitude and app insights events before unloading message assist
        await Promise.allSettled([
            flushOrCatch(
                amplitudeClients.flushEventsAsync,
                'Message Assist - Amplitude flush error'
            ),
            flushOrCatch(
                flushInsightsAsync,
                'Message Assist - App Insights flush error'
            ),
        ]);
    }

    async function flushOrCatch(
        flushFn: () => Promise<void>,
        errorMessage: string
    ) {
        try {
            await flushFn();
        } catch (error: unknown) {
            logException(errorMessage, {
                exception: new Error(getTypeSafeErrorMessage(error)),
            });
        }
    }

    const hostAppChannelName = getUrlParam('hostappchannel');

    if (hostAppChannelName === '') {
        // this warn is largely for debugging purposes when working locally from a web browser.
        console.warn(
            `Undefined host application channel, unable to post event: type: ${type}, title: ${title}, data: `,
            data
        );
        logDebugInsights({
            name: 'Message Assist Host App Channel not defined',
            properties: { type, title, data },
        });

        throw Error('Message Assist Host App Channel not defined');
    }

    const hostAppChannelVersion = getUrlParam('hostappchannelversion');

    if (hostAppChannelVersion === '') {
        throw Error('Undefined host application channel version');
    }

    const message = JSON.stringify({
        type: type,
        title: title ?? '',
        data: data ?? '',
    });

    // Native app support (Flutter InAppWebView)
    const inappwebview = getWindowObject('flutter_inappwebview');

    if (inappwebview) {
        await new Promise<void>((resolve, reject) => {
            const interval = 50; // 50ms
            let test = 40; // 40 x 50ms = 2s

            const timeout = setInterval(() => {
                const callHandler = inappwebview.callHandler;

                if (callHandler) {
                    clearInterval(timeout);
                    callHandler(hostAppChannelName, message).then(
                        () => undefined
                    );
                    resolve();
                    return;
                }

                if (--test <= 0) {
                    clearInterval(timeout);
                    reject(
                        'Undefined Message Assist channel (Undefined callHandler)'
                    );
                    return;
                }
            }, interval);
        });
        return;
    }

    // Legacy: Providence/Swedish App 9.9 (Flutter WebView)
    // Delete it when Prov/Swedish app version 9.9 is dropped.
    const channel = getWindowObject(hostAppChannelName);

    if (channel) {
        channel.postMessage(message);
        return;
    }

    // Web app support
    if (inIframe()) {
        parent.window.postMessage(message, '*');
        return;
    }

    throw Error('Fail to contact Message Assist host');
};

const parseScheduledRedirectParamValue = (value: string): unknown => {
    if (value === 'true') {
        return true;
    }

    if (value === 'false') {
        return false;
    }

    return value;
};

export const postScheduledRedirectMessage = async (target: string) => {
    const parsedTarget = new URL(target);
    const messageType = `${MESSAGE_ASSIST_SCHEMA}${parsedTarget.host}${parsedTarget.pathname}`;
    const data = Object.fromEntries(
        Array.from(parsedTarget.searchParams.entries()).map(([key, value]) => [
            key,
            parseScheduledRedirectParamValue(value),
        ])
    );

    await postMessageAssistMessage(
        messageType,
        undefined,
        JSON.stringify(data)
    );
};

// Re-export for non-UI code paths (e.g. store middleware) that need FAQ metadata.
export { getFaqDataForIntent };
export type { FaqData };

export const requestMessageAssistString = (
    messageType: string
): Promise<MessageAssistResponse | undefined> => {
    const requestId = uuidv4();
    const promiseForRequest = new Promise<MessageAssistResponse | undefined>(
        (resolve, reject) => {
            messageassistRequests.set(requestId, {
                resolve: resolve,
                reject: reject,
            } as PromiseResolveReject);
        }
    );

    let iFrameId;
    let iFrameElement;

    if (inIframe()) {
        iFrameId = MESSAGE_ASSIST_IFRAME_ID;
        try {
            iFrameElement = parent?.document?.getElementById(
                MESSAGE_ASSIST_IFRAME_ID
            );
        } catch (error: unknown) {
            // Expected with CORS requests, ex, hitting dev chatbot from local env.
            logException(
                'Message Assist - Unable to access parent document for iFrame',
                {
                    exception: new Error(getTypeSafeErrorMessage(error)),
                }
            );
        }
    }

    // Set accessibility attributes for the flutter inAppWebView iFrame if it exists
    if (iFrameId && iFrameElement) {
        // Accessibility attributes
        // TODO - consider setting these from flutter client in the future.
        iFrameElement.setAttribute('role', 'presentation');
        iFrameElement.setAttribute('aria-label', 'Grace Client Container');
        iFrameElement.setAttribute('title', 'Grace Client Container');
    }

    postMessageAssistMessage(
        messageType,
        undefined,
        JSON.stringify({
            requestId: requestId,
            receiverIFrameId: iFrameId,
        })
    ).catch((error: unknown) => {
        onMessageAssistNativeMessage(
            requestId,
            undefined,
            getTypeSafeErrorMessage(error)
        );
    });

    return promiseForRequest;
};

const onMessageAssistNativeMessage = (
    requestId: string,
    data?: string,
    error?: string
) => {
    const promiseForRequest = messageassistRequests.get(requestId);

    if (!promiseForRequest) {
        console.error('Undefined Message Assist request Id');
        return;
    }

    messageassistRequests.delete(requestId);

    if (data) {
        let json;

        try {
            json = JSON.parse(data);
        } catch (e) {
            promiseForRequest.reject(
                `Failed to parse data from native app (request Id: ${requestId})`
            );
            return;
        }

        if (json) {
            promiseForRequest.resolve(json);
        } else {
            promiseForRequest.reject(`Native app return empty json message`);
        }
    } else if (error) {
        promiseForRequest.reject(
            `Native app returned error: ${error} (request Id: ${requestId})`
        );
    } else {
        promiseForRequest.reject(
            `Native app returned empty response (request Id: ${requestId})`
        );
    }
};

if (inIframe()) {
    window.addEventListener('message', (event) => {
        let response;

        try {
            response = JSON.parse(event.data);

            if (!response.requestId) {
                return;
            }
        } catch (e) {
            // not every event is meant for message assist, so if we can't parse
            // the message, we just ignore it.
            return;
        }

        onMessageAssistNativeMessage(
            response.requestId,
            response.data,
            response.error
        );
    });
} else {
    const hostAppChannelName =
        (window.location.search.match(`hostappchannel=([^&]+)`) || [])[1] || '';

    if (hostAppChannelName !== '') {
        Object.defineProperty(window, `on${hostAppChannelName}Message`, {
            value: onMessageAssistNativeMessage,
            writable: true,
        });
    }
}

export const requestAccessTokenFromMessageAssist = (): Promise<string> => {
    return requestMessageAssistString(
        `${MESSAGE_ASSIST_SCHEMA}${MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.REQUEST_ACCESS_TOKEN}`
    )
        .then((response) => {
            if (response?.accessToken) {
                return response.accessToken;
            }

            throw Error(
                'Invalid access token response from Message Assist host'
            );
        })
        .catch((err) => {
            logException('Error retrieving health system token', {
                exception: new Error(`${err}`),
            });

            throw err;
        });
};

export const showSurveyOption = async (
    appendMessage?: (message: Activity) => void,
    sendPostBack?: (value: string) => void
) => {
    // MA greeting optimization implementation
    if (appendMessage) {
        const action = `${MESSAGE_ASSIST_SCHEMA}${MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.SURVEY_LINK_CLICKED}`;
        const attachments = [
            {
                contentType: 'application/providence.buttonlist',
                content: {
                    buttons: [
                        {
                            title: general.providence.surveyButtonLabel,
                            action,
                            data: '{}',
                            showPunchOutIcon: true,
                        },
                    ],
                },
            },
        ];
        appendMessage({
            id: 'surveyPrompt',
            text: general.providence.surveyPrompt,
            type: 'message',
            from: { name: '', role: 'bot' },
            attachments,
        });
        // Rasa implementation
    } else if (sendPostBack) {
        sendPostBack('/DIRECT.SURVEY.show_survey_option');
    }

    // Update URL param to false so survey is only presented once
    updateFaqSurveyOptionEnabled(false);

    // Notify MA that the survey message was presented
    try {
        await postMessageAssistMessage(
            `${MESSAGE_ASSIST_SCHEMA}${MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.SURVEY_OPTION_PRESENTED}`,
            undefined,
            '{}'
        );
    } catch (error: unknown) {
        logException(
            'Message Assist - Unable to send FAQ survey option presented event to MA',
            {
                exception: new Error(getTypeSafeErrorMessage(error)),
            }
        );
    }
};

// TODO: Move MA test results search button to botGreetingConfig after MA test results search experiment
export const updateMATestResultsMenuButtonAction = (
    tenantName: string,
    tenantGreetingConfig: any,
    featureFlags: AmpFlagSet
) => {
    const maTestResultsSearchFlag =
        GraceFeatureFlags.MessageAssistTestResultsSearchFlow;
    if (
        tenantName == TENANTS.MESSAGE_ASSIST &&
        featureFlags[maTestResultsSearchFlag]
    ) {
        const activity = tenantGreetingConfig.mainMenu as Activity;
        if (activity?.attachments && activity.attachments.length > 0) {
            const attachment = activity.attachments[0] as Attachment;
            const buttonList = attachment.content as any;
            const testResultsButton = buttonList.buttons.find(
                (button: any) =>
                    button.action === '/mychart.how-to-get-lab-results'
            );
            if (testResultsButton) {
                testResultsButton.action = '/testresults.search-test-result';
            }
        }
    }
};

export const getMessageAssistAnalyticId = () => getUrlParam('analyticUserId');
